import React, { useState, useEffect } from 'react';
import { TrendingUp } from 'lucide-react';
import { colors } from '../styles/colors';
import { useToast } from '../contexts/ToastContext';
import { useYear } from '../contexts/YearContext';
import PageHeader from '../components/PageHeader';
import FilterBar from '../components/FilterBar';
import AnimatedContent from '../components/AnimatedContent';
import PLCAMargeTable from '../components/pl/PLCAMargeTable';
import PLRefacturationTable from '../components/pl/PLRefacturationTable';
import { getCharges, getConsultantConfig, getConsultants, getTemps, getFacturesInternes } from '../services/airtable';
import { usePLCalculations } from '../hooks/pl/usePLCalculations';
import { getAvailableYearsFromTemps } from '../utils/yearUtils';

function PLPage() {
  const { showToast } = useToast();
  const { selectedYear, setSelectedYear, availableYears, setAvailableYears } = useYear();
  const [loading, setLoading] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);

  // Données
  const [charges, setCharges] = useState([]);
  const [consultantConfig, setConsultantConfig] = useState({});
  const [tempsData, setTempsData] = useState([]);
  const [facturesInternes, setFacturesInternes] = useState([]);
  const [consultantsMap, setConsultantsMap] = useState({});

  // Calculs via hook orchestrateur
  const calculations = usePLCalculations(
    charges,
    consultantConfig,
    tempsData,
    facturesInternes,
    selectedYear
  );

  // Chargement initial (une seule fois au montage)
  useEffect(() => {
    loadAllDataInitial();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chargement lors du changement d'année (après le chargement initial)
  useEffect(() => {
    if (!initialLoad && selectedYear !== null) {
      loadAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear]);

  // Recharger après reconnexion (session expirée → renouvelée)
  useEffect(() => {
    const handler = () => loadAllDataInitial();
    window.addEventListener('session:renewed', handler);
    return () => window.removeEventListener('session:renewed', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllDataInitial = async () => {
    try {
      setLoading(true);

      // Charger toutes les données en parallèle
      const [chargesData, configData, tempsDataRaw, facturesData, consultantsData] = await Promise.all([
        getCharges(),
        getConsultantConfig(),
        getTemps(),
        getFacturesInternes(),
        getConsultants().catch(() => [])
      ]);

      // Construire le map consultant ID -> nom
      const cMap = {};
      consultantsData.forEach(c => {
        cMap[c.id] = c.fields?.Consultant || c.fields?.Nom || c.id;
      });
      setConsultantsMap(cMap);

      // Définir les années disponibles depuis les temps
      setAvailableYears(getAvailableYearsFromTemps(tempsDataRaw));

      // Filtrer les données par année sélectionnée et enrichir avec consultantNom
      const filteredCharges = chargesData
        .filter(c => c.annee === selectedYear)
        .map(c => ({ ...c, consultantNom: c.consultant ? (cMap[c.consultant] || null) : null }));
      const filteredTemps = tempsDataRaw.filter(t => t.fields?.Année === selectedYear);
      const filteredFactures = facturesData.filter(f => f.fields?.Année === selectedYear);

      // Config consultants par année (avec résolution des noms)
      const configByYear = {};
      (Array.isArray(configData) ? configData : []).forEach(config => {
        configByYear[config.annee] = {
          nombreConsultants: config.nombreConsultants,
          consultantIds: config.consultantIds || [],
          consultantNames: (config.consultantIds || []).map(id => cMap[id]).filter(Boolean)
        };
      });

      setCharges(filteredCharges);
      setConsultantConfig(configByYear);
      setTempsData(filteredTemps);
      setFacturesInternes(filteredFactures);

      setInitialLoad(false);
    } catch (err) {
      showToast('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadAllData = async () => {
    try {
      setLoading(true);

      const [chargesData, tempsDataRaw, facturesData] = await Promise.all([
        getCharges(),
        getTemps(),
        getFacturesInternes()
      ]);

      const filteredCharges = chargesData
        .filter(c => c.annee === selectedYear)
        .map(c => ({ ...c, consultantNom: c.consultant ? (consultantsMap[c.consultant] || null) : null }));
      const filteredTemps = tempsDataRaw.filter(t => t.fields?.Année === selectedYear);
      const filteredFactures = facturesData.filter(f => f.fields?.Année === selectedYear);

      setCharges(filteredCharges);
      setTempsData(filteredTemps);
      setFacturesInternes(filteredFactures);
    } catch (err) {
      showToast('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatMontant = (montant) => {
    return montant.toLocaleString('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      fontFamily: '"Noto Sans", sans-serif'
    }}>
      <PageHeader
        icon={TrendingUp}
        title={`P&L ${selectedYear || ''}`}
        subtitle="Profit & Loss - Compte de résultat"
      />

      {/* Filtre année */}
      <FilterBar
        filters={[
          {
            type: 'select',
            value: selectedYear,
            onChange: setSelectedYear,
            options: availableYears.map(y => ({ value: y, label: y.toString() })),
            allowNull: false,
            minWidth: '120px'
          }
        ]}
        marginBottom="1.5rem"
      />

      {loading ? (
        <div style={{
          padding: '3rem',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: `4px solid ${colors.secondary.mediumGray}`,
            borderTop: `4px solid ${colors.primary.teal}`,
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{
            marginTop: '1rem',
            fontSize: '1rem',
            color: colors.primary.gray
          }}>
            Chargement...
          </p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      ) : (
        <AnimatedContent>
          <div style={{
            padding: '0 2rem 2rem 2rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <PLCAMargeTable calculations={calculations} formatMontant={formatMontant} />
            <PLRefacturationTable calculations={calculations} formatMontant={formatMontant} />
          </div>
        </AnimatedContent>
      )}
    </div>
  );
}

export default PLPage;
